import React, { useLayoutEffect, useMemo } from 'react';

import {
  getCleanroomFloorMaterial,
  type CleanroomFloorDetail,
} from '../../../utils/cleanroomFloorShared';

import { FACTORY_3D_PALETTE } from '../../../utils/factory3dStyles';



export type CleanroomFloorProps = {

  width: number;

  height: number;

  position?: [number, number, number];

  color?: string;

  darkMode?: boolean;

  receiveShadow?: boolean;

  detail?: CleanroomFloorDetail;

  autoDetail?: boolean;

  /** Tile grid + shine — off by default; fab floor is solid glass white. */

  showDecor?: boolean;

  /** High-clearcoat polished glass-white slab (inner fab floor). */

  glassWhite?: boolean;

  /** Matte sky-blue outer pad — must not share the glass-white finish. */

  surroundBlue?: boolean;

  /** Below 1: translucent slab so DXF linework reads through in 3D. */
  surfaceOpacity?: number;

  /** Draw order vs DXF tiles and other floor layers. */
  floorRenderOrder?: number;

};



/**

 * Production semiconductor cleanroom floor — glass-white fab slab or light-blue surround.

 */

export const CleanroomFloor: React.FC<CleanroomFloorProps> = ({

  width,

  height,

  position = [0, 0, 0],

  color = FACTORY_3D_PALETTE.floor.surface,

  darkMode = false,

  receiveShadow = true,

  detail: _detail,

  autoDetail: _autoDetail = true,

  showDecor: _showDecor = false,

  glassWhite: _glassWhite = false,

  surroundBlue = false,

  surfaceOpacity = 1,

  floorRenderOrder = 0,

}) => {

  void _detail;

  void _autoDetail;

  void _showDecor;

  void _glassWhite;

  const floorMaterial = useMemo(() => {
    const mat = getCleanroomFloorMaterial(darkMode).clone();

    if (darkMode) {
      return mat;
    }

    if (surroundBlue) {
      mat.color.set(color);
      mat.map = null;
      mat.roughnessMap = null;
      mat.normalMap = null;
      mat.normalScale.set(1, 1);
      mat.metalness = 0;
      mat.roughness = 0.48;
      mat.clearcoat = 0.06;
      mat.clearcoatRoughness = 0.22;
      mat.envMapIntensity = 0.28;
      mat.reflectivity = 0.12;
      mat.sheen = 0;
      mat.sheenRoughness = 1;
      mat.needsUpdate = true;
      return mat;
    }

    // Default light gray cleanroom floor (no tiles, no grout lines)
    mat.color.set(color);
    mat.map = null;
    mat.roughnessMap = null;
    mat.normalMap = null;
    mat.bumpMap = null;
    mat.normalScale.set(1, 1);
    mat.metalness = 0;
    mat.roughness = 0.52;
    mat.clearcoat = 0.04;
    mat.clearcoatRoughness = 0.3;
    mat.envMapIntensity = 0.22;
    mat.needsUpdate = true;
    return mat;
  }, [color, darkMode, surroundBlue]);

  useLayoutEffect(() => {
    const alpha = Math.min(1, Math.max(0, surfaceOpacity));
    floorMaterial.transparent = alpha < 0.999;
    floorMaterial.opacity = alpha;
    floorMaterial.depthWrite = alpha >= 0.999;
    floorMaterial.needsUpdate = true;
  }, [floorMaterial, surfaceOpacity]);

  useLayoutEffect(
    () => () => {
      floorMaterial.dispose();
    },
    [floorMaterial]
  );

  return (
    <group name="CleanroomFloor" position={position}>
      <group rotation={[-Math.PI / 2, 0, 0]}>
        <mesh receiveShadow={receiveShadow} renderOrder={floorRenderOrder}>
          <planeGeometry args={[width, height]} />
          <primitive object={floorMaterial} attach="material" />
        </mesh>
      </group>
    </group>
  );
};



export default CleanroomFloor;

